<template>
	<div
		class="pagination"
		:class="{'-t-dark': darkTheme, 'has-pager': showPager}"
	>
		<div class="pagination__left">
			<div
				v-if="showPager"
				class="pagination__pager"
			>
				<component
					:is="page !== '...' ? 'a' : 'span'"
					v-for="(page, i) in pagination"
					:key="i"
					class="pagination__page"
					:class="{ 'is-active': page === currentPage, 'is-page': page !== '...' }"
					@click="page !== '...' ? navigate(page) : () => {}"
				>
					{{ page }}
				</component>
			</div>
		</div>
		<div class="pagination__right">
			<div
				v-if="readablePagination"
				class="pagination__info"
			>
				{{ readablePagination }}
			</div>
			<div
				v-if="!showPager || showArrowControls"
				class="pagination__controls"
				:class="{'is-disabled': disabled}"
			>
				<div
					class="pagination__control"
					:class="{'is-disabled': currentPage === 1}"
					@click="currentPage !== 1 && navigate(1)"
				>
					<Icon
						icon-name="list-first"
						size="lg"
					/>
				</div>
				<div
					class="pagination__control"
					:class="{'is-disabled': currentPage === 1}"
					@click="currentPage !== 1 && navigate(Math.max(currentPage - 1, 1))"
				>
					<Icon
						icon-name="left-arrow"
						size="lg"
					/>
				</div>
				<div
					class="pagination__control"
					:class="{'is-disabled': currentPage === lastPage }"
					@click="currentPage !== lastPage && navigate(Math.min(currentPage + 1, lastPage))"
				>
					<Icon
						icon-name="right-arrow"
						size="lg"
					/>
				</div>
				<div
					class="pagination__control"
					:class="{'is-disabled': currentPage === lastPage }"
					@click="currentPage !== lastPage && navigate(lastPage)"
				>
					<Icon
						icon-name="list-last"
						size="lg"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon';

export default {
	name:       'Pagination',
	components: { Icon },
	props:      {
		disabled: {
			type:    Boolean,
			default: false,
		},
		total: {
			type:     Number,
			required: true,
		},
		perPage: {
			type:     Number,
			required: true,
		},
		currentPage: {
			type:     Number,
			required: true,
		},
		lastPage: {
			type:     Number,
			required: true,
		},
		darkTheme: {
			type:    Boolean,
			default: true,
		},
		showPager: {
			type:    Boolean,
			default: false,
		},
		showArrowControls: {
			type:    Boolean,
			default: false,
		},
	},
	computed: {
		readablePagination() {
			if (this.total === 0) {
				return null;
			}
			const countFrom = (this.currentPage * this.perPage) - this.perPage + 1;
			const countTo = (this.currentPage * this.perPage);
			const total = this.total;
			return `${countFrom}-${Math.min(countTo, total)} ${this.$I18n.trans('commons.of')} ${total}`;
		},
		pages() {
			return Array.from({ length: this.lastPage }, (v, k) => k + 1);
		},
		pagination() {
			const pagination = [];
			const pageRange = 3;

			this.pages.forEach((page, index) => {
				if (
					page === 1 ||
					page === this.pages.length ||
					(page >= this.currentPage - pageRange && page <= this.currentPage + pageRange)
				) {
					pagination.push(page);
				} else if (
					page - 1 === pagination[pagination.length - 1] ||
					(index === 0 && page === this.currentPage - pageRange - 1)
				) {
					pagination.push('...');
				}
			});

			return pagination;
		},
	},
	methods: {
		navigate(page) {
			this.$emit('change', page);
		},
		getPages() {
			const pages = [];
			let page = 1;
			while (page <= this.lastPage) {
				pages.push(page);
				page++;
			}
			return pages;
		},
		clampPages(pages) {
			// Given an array of pages
			// take the first page, the last page and the current page + 3 pages before and after
			return [
				1,
				...this.getPagesAroundCurrentPage(pages),
				this.lastPage,
			];
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.pagination {
	--color-pagination-text: var(--color-black);
	--color-pagination-controls-enabled: var(--color-grey-600);
	--color-pagination-controls-disabled: var(--color-grey-300);
	--color-pagination-controls-focus: var(--color-green-petrol);
	--color-numbered-pager: var(--color-black);
	--color-numbered-pager-active: var(--color-white);
	--color-numbered-pager-background-active: var(--color-green-petrol);
	--color-pagination-info: var(--color-black);

	display: flex;
	justify-content: space-between;
	align-items: center;

	&.-t-dark {
		--color-pagination-text: var(--color-white);
		--color-pagination-controls-enabled: var(--color-grey-300);
		--color-pagination-controls-disabled: var(--color-grey-600);
		--color-pagination-controls-focus: var(--color-cta, var(--color-hype-yellow));
		--color-numbered-pager: var(--color-white);
		--color-numbered-pager-active: var(--color-black);
		--color-numbered-pager-background-active: var(--color-white);
		--color-pagination-info: var(--color-white);
	}

	body.-t-light & { // stylelint-disable-line
		--color-pagination-text: var(--color-black);
		--color-pagination-controls-enabled: var(--color-grey-300);
		--color-pagination-controls-disabled: var(--color-grey-600);
		--color-pagination-controls-focus: var(--color-hype-yellow);
		--color-numbered-pager: var(--color-black);
		--color-numbered-pager-active: var(--color-white);
		--color-numbered-pager-background-active: var(--color-black);
		--color-pagination-info: var(--color-black);
	}
}

.pagination__pager {
	display: flex;
	gap: $sp1;

	@include size(sm-up) {
		gap: $sp2;
	}
}

.pagination__right {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	user-select: none;

	.pagination.has-pager & {
		@include size(xs-only) {
			display: none;
		}
	}
}

.pagination__pages {
	list-style: none;
}

.pagination__page {
	@include font-size(fs-80);
	width: $sp6;
	height: $sp6;
	border-radius: 50%;
	display: flex;
	justify-content: center;
	align-items: center;
	color: var(--color-numbered-pager);
	transition: none;

	&:not(.is-page) {
		cursor: default;
		width: $sp3;
	}

	&.is-active {
		color: var(--color-numbered-pager-active);
		background-color: var(--color-numbered-pager-background-active);
	}

	&.is-page:hover {
		color: var(--color-numbered-pager-active);
		background-color: var(--color-numbered-pager-background-active);
	}
}

.pagination__info {
	color: var(--color-pagination-info);

	&:not(:only-child) {
		margin-right: $sp5;
	}
}

.pagination__controls {
	display: flex;
	margin-right: -#{$sp3};

	&.is-disabled {
		pointer-events: none;
	}

	@include size(md-up) {
		margin-right: -#{$sp4};
	}
}

.pagination__control {
	color: var(--color-pagination-controls-enabled);
	padding: $sp3;
	cursor: pointer;

	&.is-disabled {
		cursor: default;
		color: var(--color-pagination-controls-disabled);
	}

	&:not(.is-disabled):hover {
		color: var(--color-pagination-controls-focus);
	}

	.pagination__controls.is-disabled & {
		color: var(--color-pagination-controls-disabled);
	}

	@include size(md-up) {
		padding: $sp4;
	}
}
</style>
